import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../data/site-content';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const ServiceLayout = ({ 
  title = "", 
  subtitle = "", 
  description = "", 
  content = [], 
  metaTitle = "", 
  metaDescription = "",
  ctaText = "Book første session (375 kr)",
  image = null,
  actions = null,
  children = null,
  footer = null,
  showContentSection = true,
  categoryLabel = "Behandling"
}) => {
  // Sikrer at description er en streng og splitter den i afsnit
  const paragraphs = typeof description === 'string' ? description.split('\n\n').filter(p => p.trim() !== '') : [];

  return (
    <>
      <Helmet>
        <title>{metaTitle || `${title} – ${siteConfig.name}`}</title>
        <meta name="description" content={metaDescription || subtitle} />
      </Helmet>

      <section className="pt-20 pb-0 bg-[#FAF7F2]">
        <div className="max-w-6xl mx-auto px-6">
          {title === "Autismevenlige sessioner & forløb" || title === "Autismevenlig session og forløb" ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              <div className="text-[#3B352F]/70 text-lg leading-relaxed max-w-none">
                <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2 mb-4">
                  <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                  Autismevenlig session og forløb
                </span>
                <h1 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-8 leading-tight">
                  Terapi med tydelighed, ro og færre overraskelser
                </h1>
                <p className="mb-8">
                  Du behøver ikke passe ind i en bestemt måde at gå i terapi på. Jeg tilpasser rammerne, kommunikationen og tempoet, så sessionen bliver mere overskuelig og tryg for dig.
                </p>
                
                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <Link 
                    to="/bestil-tid" 
                    className="inline-flex items-center justify-center gap-2 bg-[#193f10] text-white px-6 py-3.5 rounded-lg font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md"
                  >
                    Book første behandlingssession
                  </Link>
                  <a 
                    href="#forloeb-model" 
                    className="inline-flex items-center justify-center gap-2 border-2 border-[#193f10] text-[#193f10] px-6 py-3.5 rounded-lg font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all"
                  >
                    Se hvordan det foregår
                  </a>
                </div>
              </div>

              {/* Højre kolonne: Den visuelle før/under/efter model */}
              <div className="bg-white border border-[#E8DDCE] rounded-3xl p-8 md:p-10 flex flex-col justify-between shadow-sm relative overflow-hidden">
                <div className="absolute -right-6 -top-6 w-20 h-24 rounded-full bg-[#123F13]/5 pointer-events-none" />
                
                <div className="relative z-10">
                  <h3 className="text-2xl font-serif text-[#123F13] mb-6">Du ved, hvad der skal ske</h3>
                  
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-sm">
                        1
                      </div>
                      <div>
                        <h4 className="font-bold text-[#123F13] mb-1">Før sessionen</h4>
                        <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                          Du kan få tydelig information om rammer, sted og næste skridt.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-sm">
                        2
                      </div>
                      <div>
                        <h4 className="font-bold text-[#123F13] mb-1">Under sessionen</h4>
                        <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                          Roligt tempo, konkrete spørgsmål og mulighed for pauser.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-sm">
                        3
                      </div>
                      <div>
                        <h4 className="font-bold text-[#123F13] mb-1">Efter sessionen</h4>
                        <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                          Jeg gør næste skridt tydeligt, så du ikke skal gætte dig frem.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#E8DDCE] text-center">
                    <p className="text-sm font-medium text-[#8C2F12] italic">
                      Du må gerne sige, hvad der hjælper dig. Der er ikke én rigtig måde at være klient på.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`grid grid-cols-1 ${image ? 'lg:grid-cols-2' : ''} gap-12 items-center mb-12`}>
              <div className="text-[#3B352F]/70 text-lg leading-relaxed max-w-none">
                <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2 mb-4">
                  <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                  {categoryLabel}
                </span>
                <h1 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-6">{title}</h1>
                <p className="text-xl text-[#8C2F12] font-medium mb-8 leading-relaxed">
                  {subtitle}
                </p>
                
                {/* Mobil-billede: Vises kun på mobil/tablet lige under underoverskriften */}
                {image && (
                  <div className="block lg:hidden my-8 rounded-2xl shadow-lg border border-[#E8DDCE] w-full">
                    <img 
                      src={image} 
                      alt={title} 
                      loading="eager"
                      className="w-full h-auto block rounded-2xl"
                    />
                  </div>
                )}

                {paragraphs.map((para, i) => (
                  <p key={i} className="mb-6">{para}</p>
                ))}

                {actions && (
                  <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 whitespace-nowrap">
                    {actions}
                  </div>
                )}
              </div>
              
              {/* Desktop-billede: Vises kun på store skærme (lg og op) */}
              {image && (
                <div className="hidden lg:block relative lg:aspect-square rounded-2xl overflow-hidden shadow-xl border border-[#E8DDCE] w-full">
                  <img 
                    src={image} 
                    alt={title} 
                    loading="eager"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          )}

          {/* Hvordan foregår behandlingen sektion */}
          {showContentSection && content && content.length > 0 && (
            <div className="mb-20">
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-10">Hvordan foregår behandlingen?</h2>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {content.map((item, index) => {
                  const hasColon = item.includes(':');
                  const titlePart = hasColon ? item.split(':')[0].trim() : `Trin ${index + 1}`;
                  const descPart = hasColon ? item.split(':').slice(1).join(':').trim() : item;
                  
                  // Dynamisk layout baseret på antal punkter
                  const totalItems = content.length;
                  let colSpanClasses = "";

                  if (totalItems === 4) {
                    // 4 punkter: 4 på række (12 / 4 = 3)
                    colSpanClasses = "md:col-span-6 lg:col-span-3";
                  } else if (totalItems === 5) {
                    // 5 punkter: 3 på første række (col-4), 2 på næste (col-6)
                    colSpanClasses = index >= 3 ? "md:col-span-6 lg:col-span-6" : "md:col-span-4 lg:col-span-4";
                  } else if (totalItems === 3) {
                    // 3 punkter: 3 på række (12 / 3 = 4)
                    colSpanClasses = "md:col-span-4 lg:col-span-4";
                  } else {
                    // Standard fallback
                    colSpanClasses = "md:col-span-6 lg:col-span-4";
                  }
                  
                  return (
                    <div 
                      key={index} 
                      className={`bg-white p-8 rounded-2xl border border-[#E8DDCE] shadow-sm hover:shadow-md transition-all group relative overflow-hidden ${colSpanClasses}`}
                    >
                      <div className="absolute -top-4 -right-4 text-6xl font-serif text-[#123F13]/5 font-bold group-hover:text-[#8C2F12]/10 transition-colors">
                        {(index + 1).toString().padStart(2, '0')}
                      </div>
                      <div className="relative z-10">
                        <span className="text-[#8C2F12] font-bold text-sm mb-2 block">{(index + 1).toString().padStart(2, '0')}</span>
                        <h3 className="text-xl font-bold text-[#123F13] mb-3">{titlePart}</h3>
                        <p className="text-[#3B352F]/70 leading-relaxed">
                          {descPart}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="max-w-6xl mx-auto">
            {children}

            {footer}
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceLayout;
